import React, { useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './index.module.scss';
import MochiPayFlowSrc from 'assets/mochi_pay_flow.png';
import iconAlert from 'assets/icon_alert.svg';
import Icon from 'components/icon';
import Info from 'components/info';
import PageLayout from 'layout/page';
import CardItemSwitch from 'components/card-item-switch';
import ButtonDialog from 'components/button-dialog';
import Dialog from 'components/dialog';
import {
  useMochiPayFlowContext,
  INITIALIZE,
  CHANGE_REMOVE_DIALOG_SHOW,
  CHANGE_UPDATE_DIALOG_SHOW,
} from '../use-mochi-pay-flow';
import { PATH, helperUrl } from 'config';
import { SET_CUSTOMER_SERVICE_DIALOG, useAppContext } from 'use-app';
import amplitude from 'service/amplitude';
import { openLink } from 'helpers/link-helper';

const SwitchContent = switchState => {
  if (switchState) {
    return '已綁定「中油Pay」扣款';
  } else {
    return '尚未綁定「中油Pay」扣款';
  }
};

const NotDefaultDistributor = () => {
  const { dispatch } = useMochiPayFlowContext();
  const onButtonClick = () => {
    dispatch({ type: CHANGE_UPDATE_DIALOG_SHOW, payload: true });
  };

  return (
    <div className={styles['not-default-distributor']}>
      <Icon imgSrc={iconAlert} height="28px" alt="alert-icon" />
      <div>
        若要繼續使用「中油Pay」的麻吉付代扣功能，
        <span onClick={onButtonClick}>請點此更新您的付款方式 »</span>
      </div>
    </div>
  );
};

const LastPaymentFail = () => {
  const history = useHistory();
  const onButtonClick = () => {
    history.push(PATH.MOCHIPAY_FLOW_CONFIRM);
  };

  return (
    <div className={styles['last-payment-fail']}>
      <Icon imgSrc={iconAlert} height="28px" alt="alert-icon" />
      <div>
        交易授權失敗，請前往「中油Pay > 支付管理」重新設定您的卡片，
        <span onClick={onButtonClick}>再點此重新綁定 »</span>
      </div>
    </div>
  );
};

const ContentDisplay = () => {
  const { state } = useMochiPayFlowContext();
  switch (state.state) {
    case 'not-default-distributer':
      return <NotDefaultDistributor />;
    case 'last-payment-fail':
      return <LastPaymentFail />;
    default:
      return <div />;
  }
};

const RemoveDialog = () => {
  const { state, dispatch, removeWallet } = useMochiPayFlowContext();
  const closeMochiPay = () => {
    amplitude.payment_linkCard_dialog_unlink_onClick();
    removeWallet();
  };
  const closeDialog = () => {
    amplitude.payment_linkCard_dialog_cancel_onClick();
    dispatch({ type: CHANGE_REMOVE_DIALOG_SHOW, payload: false });
  };

  return (
    <ButtonDialog
      title="您確定要關閉停車代扣嗎?"
      horizonal
      items={[
        {
          content: '確定關閉',
          theme: 'positive',
          onClick: closeMochiPay,
          beLoading: true,
        },
        {
          content: '取消',
          theme: 'cancel',
          onClick: closeDialog,
        },
      ]}
      isShow={state.removeDialogShow}
    />
  );
};

const UpdateDialog = () => {
  const { state, dispatch, updateDefaultDistributor } = useMochiPayFlowContext();
  const onUpdateClick = () => {
    updateDefaultDistributor();
  };
  const onCloseClick = () => {
    dispatch({ type: CHANGE_UPDATE_DIALOG_SHOW, payload: false });
  };
  return (
    <Dialog
      title="付款方式重要提醒"
      message="您是否要更新「中油Pay」為預設付款通路？下次使用麻吉付時，將由「中油Pay」設定的信用卡扣款。"
      button="立即更新"
      onConfirm={onUpdateClick}
      withClose={true}
      onDismiss={onCloseClick}
      isShow={state.updateDialogShow}
    />
  );
};

const MochiPaySetting = () => {
  const history = useHistory();
  const location = useLocation();
  const { state, dispatch } = useMochiPayFlowContext();
  const { dispatch: appDispatch } = useAppContext();

  useEffect(() => {
    dispatch({ type: INITIALIZE });
  }, [dispatch]);

  const onSwitchClick = () => {
    if (state.switchChecked) {
      amplitude.payment_bindCard_onCancel(); //deprecate
      amplitude.payment_linkCard_confirm_onClick();
      dispatch({ type: CHANGE_REMOVE_DIALOG_SHOW, payload: true });
    } else {
      amplitude.payment_bindCard_onClick();
      history.push(PATH.MOCHIPAY_FLOW_CONFIRM);
    }
  };

  const customerServiceDialogShow = useCallback(() => {
    appDispatch({
      type: SET_CUSTOMER_SERVICE_DIALOG,
      payload: true,
    });
  }, [appDispatch]);

  const customerServiceDialogHide = useCallback(() => {
    appDispatch({
      type: SET_CUSTOMER_SERVICE_DIALOG,
      payload: false,
    });
  }, [appDispatch]);

  return (
    <>
      <PageLayout>
        <Info
          theme="blue"
          withArrow={true}
          onClick={customerServiceDialogShow}
          customStyle={{ margin: '-30px -24px 20px' }}
        >
          若您遇到付款設定問題，請聯絡麻吉付客服
        </Info>
        <ButtonDialog
          isShow={state.customerServiceDialog}
          title="聯絡麻吉付客服"
          items={[
            {
              content: '幫助中心',
              theme: 'positive',
              onClick: () => openLink(helperUrl),
            },
            {
              content: '客服信箱',
              theme: 'positive',
              onClick: () => {
                history.push({
                  pathname: PATH.CONTACT,
                  state: { from: location.pathname, issueGroupKey: 'Account', issueKey: 'Account::CreditCardBinding' },
                });
              },
            },
            {
              content: '取消',
              theme: 'cancel',
              onClick: customerServiceDialogHide,
            },
          ]}
        />

        <div className={styles['mochi-pay-setting']}>
          <div className={styles['body']}>
            <div>
              <div className={styles['title']}>麻吉付付款設定</div>
              <div className={styles['switch']}>
                <CardItemSwitch
                  title="麻吉付"
                  switchState={state.switchState}
                  subText={SwitchContent(state.switchChecked)}
                  isChecked={state.switchChecked}
                  onChange={onSwitchClick}
                />
              </div>
              <div className={styles['content']}>
                <ContentDisplay />
              </div>
            </div>
          </div>
          <div className={styles['footer']}>
            <img src={MochiPayFlowSrc} width="200px" height="200px" alt="mochi-pay-flow" />
            <div className={styles['hint']}>
              開啟停車代扣後，代表您可以在地圖上有黃色
              <br />
              麻吉付圖示的停車場使用麻吉付代扣功能！
            </div>
          </div>
          <RemoveDialog />
          <UpdateDialog />
        </div>
      </PageLayout>
    </>
  );
};

export default MochiPaySetting;
